import React, { useEffect, useState } from 'react';

import { IDeviceAwareModel } from '~/src/dash/api/console-api';
import { IInputRow } from '~/src/dash/components/input-row';
import { useDebounce } from '~/src/dash/utils/hooks';

export const AppOverrideStateKeys = {
  STOREURL: 'AppStoreURL',
  NAME: 'AppNameOverride',
  DOMAIN: 'AppDomainOverride',
  BUNDLE: 'AppBundleOverride',
};

export type AppOverrideStateKey =
  | 'AppStoreURL'
  | 'AppNameOverride'
  | 'AppDomainOverride'
  | 'AppBundleOverride';

interface IRewriteInputRowObjectsProps {
  stateKey: string;
  data: IDeviceAwareModel;
  devices: string[];
  handleChange: (value: any) => void;
  selectTitle: string;
  textfieldTitle: string;
  placeholderText: string;
  addButtonText: string;
}

export interface IRowInfo {
  device: string;
  text?: string;
}

interface IInputProps {
  e: any;
  idx: number;
}

export interface IHandleChangeAppOverrideProps {
  rows: IRowInfo[];
  stateKey: AppOverrideStateKey;
}

const activeRows = (devices: string[], stateObject: any): IRowInfo[] => {
  const result: IRowInfo[] = [];
  devices.map((device) => {
    if (stateObject[device]) {
      result.push({ device, text: stateObject[device] });
    }
  });
  return result;
};

const findInactiveItems = (devices: string[], currentRows: IRowInfo[]): string[] => {
  const activeItems: string[] = [];
  currentRows.map((row) => {
    activeItems.push(row.device);
  });
  const filteredItems = devices.filter((device) => !activeItems.includes(device));
  return filteredItems;
};

export const RewriteInputRowObjects:
  | React.FunctionComponent<IRewriteInputRowObjectsProps>
  | any = ({
  stateKey,
  data,
  devices = [],
  handleChange,
  selectTitle,
  textfieldTitle,
  placeholderText = '',
  addButtonText = 'Add Row',
}: IRewriteInputRowObjectsProps): IInputRow[] => {
  const [rows, setRows] = useState(activeRows(devices, data));
  const debouncedRows = useDebounce(rows, 500);
  const inactiveItems = findInactiveItems(devices, rows);

  useEffect(() => {
    handleChange({ rows: [...rows], stateKey });
  }, [debouncedRows]);

  const handleSelect = (inputProps: IInputProps) => {
    const { e, idx } = inputProps;
    const cloneRows = [...rows];
    cloneRows[idx].device = e.target.value;
    setRows(cloneRows);
    handleChange({ rows: cloneRows, stateKey });
  };

  const handleTextFieldChange = (inputProps: IInputProps) => {
    const { e, idx } = inputProps;
    const cloneRows = [...rows];
    cloneRows[idx].text = e.target.value;
    setRows(cloneRows);
  };

  const handleAdd = () => {
    setRows([...rows, { device: inactiveItems[0], text: '' }]);
  };

  const handleRemove = (idx: number) => {
    const cloneRows = [...rows];
    cloneRows.splice(idx, 1);
    setRows(cloneRows);
    handleChange({ rows: cloneRows, stateKey });
  };

  const inputRows = rows.map((row, idx) => {
    const { device, text } = row;
    const title = device === 'Xbox' ? 'Microsoft or Xbox' : device;
    const items = [device, ...inactiveItems];
    // items.unshift(device)
    return {
      renderType: 'SelectTextfieldRemove',
      idx,
      // SELECT
      title1: selectTitle,
      simpleSelectItems: items,
      value1: device,
      handleChange1: handleSelect,
      // TEXTFIELD
      title2: textfieldTitle,
      value2: text,
      placeholderText,
      handleChange2: handleTextFieldChange,
      // REMOVE BUTTON
      handleClick1: handleRemove,
      // OPTIONAL
      isActiveValue1: () => !!device && device !== '',
      getActiveTag1: (active: boolean) => (active ? title : ''),
    } as IInputRow;
  });

  const addButtonRow = {
    renderType: 'AddRowModelSecondaryButton',
    title1: addButtonText,
    handleClick1: handleAdd,
    disabled1: inactiveItems.length === 0,
    // Props below are mandatory for IInputRow
    // but are not applicable to this component
    isActiveValue1: () => false,
    getActiveTag1: (active: boolean) => (active ? '' : ''),
  } as IInputRow;

  const allRows = () => {
    inputRows.push(addButtonRow);
    return inputRows;
  };

  return allRows();
};
